import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const fetchSupplierProfileInfo = createAsyncThunk(
  'suppliersDashboard/fetchSupplierProfileInfo',
  async ({ supplierId }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers/supplierProfile/supplierInfo`, {
        signal,
        params: { supplierID: supplierId },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('suppliersDashboard/fetchSupplierProfileInfo failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch supplier profile');
    }
  }
);

export const updateSupplierInformation = createAsyncThunk(
  'suppliersDashboard/updateSupplierInformation',
  async ({ actionType, sectionName, updatedValues, email, rowId }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${API_URL}/api/suppliers/UpdatingInformation`,
        {
          Action: actionType,
          SectionName: sectionName,
          UpdatedValues: updatedValues,
          email,
          RowID: rowId,
        },
        { signal, headers: { 'Content-Type': 'application/json' } }
      );
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('suppliersDashboard/updateSupplierInformation failed', e);
      return rejectWithValue(e?.response?.data?.message || e?.message || 'Failed to update supplier');
    }
  }
);

export const fetchSuppliersServiceOptions = createAsyncThunk(
  'suppliersDashboard/fetchServiceOptions',
  async (_, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers/serviceOptions`, { signal });
      return Array.isArray(res.data) ? res.data : [];
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('suppliersDashboard/fetchServiceOptions failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch service options');
    }
  }
);

export const fetchSuppliersPageData = createAsyncThunk(
  'suppliersDashboard/fetchPageData',
  async ({ searchValue = '', serviceFilter = '' }, { signal, rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/api/suppliers/pageData`, {
        signal,
        params: { searchValue, serviceFilter },
      });
      return res.data;
    } catch (e) {
      if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') throw e;
      console.error('suppliersDashboard/fetchPageData failed', e);
      return rejectWithValue(e?.message || 'Failed to fetch suppliers page data');
    }
  }
);

const slice = createSlice({
  name: 'suppliersDashboard',
  initialState: {
    supplierProfile: null,
    supplierProfileLoading: false,
    supplierProfileError: null,
    pageData: null,
    pageDataLoading: false,
    pageDataError: null,
    serviceOptions: [],
    serviceOptionsLoading: false,
    serviceOptionsError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSupplierProfileInfo.pending, (state) => {
        state.supplierProfileLoading = true;
        state.supplierProfileError = null;
      })
      .addCase(fetchSupplierProfileInfo.fulfilled, (state, action) => {
        state.supplierProfileLoading = false;
        state.supplierProfile = action.payload ?? null;
      })
      .addCase(fetchSupplierProfileInfo.rejected, (state, action) => {
        state.supplierProfileLoading = false;
        if (action?.meta?.aborted) return;
        state.supplierProfileError = action.payload || action.error?.message || 'Failed to fetch supplier profile';
      })

      .addCase(fetchSuppliersServiceOptions.pending, (state) => {
        state.serviceOptionsLoading = true;
        state.serviceOptionsError = null;
      })
      .addCase(fetchSuppliersServiceOptions.fulfilled, (state, action) => {
        state.serviceOptionsLoading = false;
        state.serviceOptions = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchSuppliersServiceOptions.rejected, (state, action) => {
        state.serviceOptionsLoading = false;
        if (action?.meta?.aborted) return;
        state.serviceOptionsError = action.payload || action.error?.message || 'Failed to fetch service options';
      })

      .addCase(fetchSuppliersPageData.pending, (state) => {
        state.pageDataLoading = true;
        state.pageDataError = null;
      })
      .addCase(fetchSuppliersPageData.fulfilled, (state, action) => {
        state.pageDataLoading = false;
        state.pageData = action.payload ?? null;
      })
      .addCase(fetchSuppliersPageData.rejected, (state, action) => {
        state.pageDataLoading = false;
        if (action?.meta?.aborted) return;
        state.pageDataError = action.payload || action.error?.message || 'Failed to fetch suppliers page data';
      });
  },
});

export default slice.reducer;


